import { useState } from "react"

import { Tabs, Tooltip, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"

import Button from "../../../../../../Components/Button"
import { ENRICHMENT_FIELD_COUNT, enrichmentFillCount } from "../../schema/enrichment"
import { EVIDENCE_IMAGE_TYPE_COUNT, evidenceImageFillCount } from "../../schema/evidenceImages"
import EnrichmentDataFields from "./EnrichmentDataFields/EnrichmentDataFields"
import EnrichmentImages from "./EnrichmentImages/EnrichmentImages"

import detailStyles from "../../detail.module.less"
import styles from "./enrichmentPane.module.less"

const { Text } = Typography

// The Enrichment tab: an inner split between the data-point form and the per-dispute
// images, each side carrying its own fill counter, plus the Save & Update PDF bar that
// feeds both back into a fresh evidence render.
//
// `active` rather than the caller rendering us conditionally: the page tabs would
// otherwise unmount this on every switch away and lose which sub-tab was open.
const EnrichmentPane = ({
  active,
  enrichmentValues,
  onEnrichmentChange,
  evidenceImages,
  onEvidenceImagesChange,
  isActionable,
  enrichmentDirty,
  actioning,
  regenerating,
  onSave
}) => {
  const { t } = useTranslation()
  const [subTab, setSubTab] = useState("data")

  if (!active) return null

  const countLabel = (label, n, total) => (
    <span className={detailStyles.tabLabel}>
      {t(label)}
      <Text type="secondary" className={detailStyles.tabCount}>
        {t("_n_/_total_", { n, total })}
      </Text>
    </span>
  )

  return (
    <div className={cls(detailStyles.rail, detailStyles.railTab, styles.pane)}>
      <div className={cls(detailStyles.railScroll, styles.paneScroll)}>
        <Tabs
          className={styles.subtabs}
          activeKey={subTab}
          onChange={setSubTab}
          items={[
            {
              key: "data",
              label: countLabel(
                "Data fields",
                enrichmentFillCount(enrichmentValues),
                ENRICHMENT_FIELD_COUNT
              )
            },
            {
              key: "images",
              label: countLabel(
                "Images",
                evidenceImageFillCount(evidenceImages),
                EVIDENCE_IMAGE_TYPE_COUNT
              )
            }
          ]}
        />
        {subTab === "images" ? (
          <EnrichmentImages
            images={evidenceImages}
            onChange={onEvidenceImagesChange}
            isActionable={isActionable}
          />
        ) : (
          <EnrichmentDataFields
            values={enrichmentValues}
            onChange={onEnrichmentChange}
            isActionable={isActionable}
          />
        )}
      </div>
      {/* Floats over the scrolling form, pinned bottom-centre the same way the PDF
          canvas pins its toolbar, so it stays reachable however far down the merchant
          is. Standard primary Button, only the placement is borrowed. Disabled until
          something changes, with the reason on hover so a greyed-out primary button is
          never a dead end. */}
      {isActionable ? (
        <div className={styles.save}>
          <Tooltip title={enrichmentDirty ? "" : t("Edit a field to update the PDF.")}>
            <span>
              <Button
                type="primary"
                size="large"
                block
                className="btn-primary_animation"
                onClick={onSave}
                disabled={!enrichmentDirty || actioning}
                loading={regenerating}
                text={t("Save & Update PDF")}
              />
            </span>
          </Tooltip>
        </div>
      ) : null}
    </div>
  )
}

export default EnrichmentPane
